import React, {Suspense, useCallback} from 'react';
import {useEnvironmentStore} from '@di-shared/features/environment/environmentStore';
import {BrandType, ThemeType} from '@shared/cci/features/const/environment';
import {ErrorBoundary} from 'react-error-boundary';
import {useSuspenseQuery} from '@tanstack/react-query';
import {getQueryKey} from '@shared/queryKeyMap';
import Lottie, {LottieComponentProps} from 'lottie-react';

export type LottieSelectorWrapperProps = Omit<LottieComponentProps, 'animationData'> & {
  kiaLightSrc: () => Promise<{default: unknown}>;
  kiaDarkSrc: () => Promise<{default: unknown}>;
  hyundaiLightSrc: () => Promise<{default: unknown}>;
  hyundaiDarkSrc: () => Promise<{default: unknown}>;
  genesisLightSrc: () => Promise<{default: unknown}>;
  genesisDarkSrc: () => Promise<{default: unknown}>;
};

function LottieSelectorWrapper({
  kiaLightSrc,
  kiaDarkSrc,
  hyundaiLightSrc,
  hyundaiDarkSrc,
  genesisLightSrc,
  genesisDarkSrc,
  ...props
}: LottieSelectorWrapperProps) {
  const theme = useEnvironmentStore((state) => state.theme);
  const brand = import.meta.env.VITE_BRAND;

  const selectImage = useCallback(async () => {
    let imgSrc;
    if (brand === BrandType.KIA) {
      if (theme === ThemeType.LIGHT) {
        imgSrc = await kiaLightSrc();
      } else {
        imgSrc = await kiaDarkSrc();
      }
    } else if (brand === BrandType.HYUNDAI) {
      if (theme === ThemeType.LIGHT) {
        imgSrc = await hyundaiLightSrc();
      } else {
        imgSrc = await hyundaiDarkSrc();
      }
    } else {
      if (theme === ThemeType.LIGHT) {
        imgSrc = await genesisLightSrc();
      } else {
        imgSrc = await genesisDarkSrc();
      }
    }
    return imgSrc.default;
  }, [theme]);

  return (
    <ErrorBoundary fallback={<></>}>
      {/* FIXME img tag에는 skeleton 마크업이 먹지 않음 */}
      <Suspense fallback={<></>}>
        <LottieSelector
          lottieFetcher={() => selectImage()}
          {...props}
        />
      </Suspense>
    </ErrorBoundary>
  );
}

export default LottieSelectorWrapper;

export type LottieSelectorProps = Omit<LottieComponentProps, 'animationData'> & {
  lottieFetcher(): Promise<unknown>;
};

function LottieSelector({lottieFetcher, ...props}: LottieSelectorProps) {
  const theme = useEnvironmentStore((state) => state.theme);
  const brand = import.meta.env.VITE_BRAND;

  const {data: lottieSrc} = useSuspenseQuery({
    queryKey: [getQueryKey(lottieFetcher), theme, brand],
    queryFn: () => lottieFetcher(),
  });

  if (!lottieSrc) {
    return <div className="skeleton" />;
  }

  return (
    <Lottie
      animationData={lottieSrc as unknown}
      {...props}
    />
  );
}
